import React, { useContext, useEffect, useState, useRef } from 'react'
import classNames from 'classnames'

import { Icon } from '@scentregroup/ui'
import { Accordion } from '@hub/accordion'

import analyticsUtils from '../../../helpers/analytics-utils'
import trackEvent from '../../../helpers/analytics-utils/track-event'
import { PaneAccordion } from '../pane-accordion'
import type { Item, ParentItem } from '../types'
import { LinkContext } from './context'

import style from './index.module.scss'

const attrsAllowList = ['target', 'rel', 'title']

const Pane: React.FC<{
  depth?: number
  title?: string
  items: Item[]
  initialPath?: number[]
  onClose?: () => void
  onExit?: () => void
}> = ({ depth = 0, title, items, initialPath, onClose, onExit }) => {
  const Link = useContext(LinkContext)

  const [path, setPath] = useState<number[] | undefined>(initialPath)

  const [expandedIndex, setExpandedIndex] = useState<
    number | number[] | undefined
  >()

  useEffect(() => setPath(initialPath), [initialPath])

  // to get the default accordion index (this will be use on mobile)
  let accordionIndex = [0]
  // count the number of items that does not use accordion
  const noItemCnt = items.filter(
    item => (item as ParentItem)?.items === undefined
  ).length
  if (initialPath && depth === 1) {
    accordionIndex = [initialPath[0] - noItemCnt]
  }

  // to scroll pane to the top based on the expanded index
  const latestAccordion = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = latestAccordion.current

    if (!el) {
      return
    }

    // to check if the div is already visible then it will anchor on that element to avoid scrolling
    const height1 = el.lastElementChild?.lastElementChild?.clientHeight ?? 0
    const height2 =
      el.lastElementChild?.lastElementChild?.lastElementChild?.clientHeight ?? 0
    if (height1 > 0 && height2 > 0 && height1 === height2) {
      // track event for mobile automatic scroll pane
      trackAccordionPane(accordionIndex[0])
      return
    }

    setExpandedIndex(accordionIndex)
  })

  useEffect(() => {
    latestAccordion.current?.scrollIntoView()
  }, [expandedIndex])

  // this is used to track event for accordion pane
  const trackAccordionPane = (index: number | number[] | undefined): void => {
    items.forEach((item, i) => {
      if (item) {
        if ('items' in item && i - noItemCnt === index && depth === 1) {
          trackEvent(
            analyticsUtils.categories.UNIVERSAL_HEADER,
            analyticsUtils.actions.UNIVERSAL_HEADER_NAV_MENU_OPEN,
            `L1-${i} ${item.title}`
          )
        }
      }
    })
  }

  return (
    <>
      <div
        className={classNames(style.pane)}
        data-test-id={`main-navdrawer-${depth}`}
      >
        {title && <div className={classNames(style.title)}>{title}</div>}
        <Accordion
          allowToggle
          defaultIndex={accordionIndex}
          onChange={index => {
            trackAccordionPane(index)
          }}
        >
          {items.map((item, i) => {
            if (!item) {
              return null
            }

            if ('url' in item) {
              const attrs = item.attrs

              return (
                <Link
                  {...(attrs &&
                    attrsAllowList.reduce(
                      (memo, key) => ({ ...memo, [key]: attrs[key] }),
                      {}
                    ))}
                  key={i}
                  to={item.url}
                  className={classNames(
                    item.className,
                    style.link,
                    style.item,
                    depth === 1 && 'key' in item ? style.linkBold : null
                  )}
                  data-test-id={`main-navdrawer-link-${depth}-${i}`}
                  onClick={() => {
                    onClose?.()
                    trackEvent(
                      analyticsUtils.categories.UNIVERSAL_HEADER,
                      analyticsUtils.actions.UNIVERSAL_HEADER_NAV_LINK_CLICK,
                      `L${depth}-${i} ${item.label}`
                    )
                  }}
                >
                  {item.label}
                </Link>
              )
            }

            if ('items' in item) {
              // third level pane accordion
              if (depth === 1) {
                return (
                  <div
                    className={classNames(style['accordion-scroll-mobile'])}
                    key={i}
                    ref={i === path?.[0] ? latestAccordion : null}
                  >
                    <PaneAccordion
                      itemCnt={i}
                      itemTitle={item.title}
                      items={item.items}
                    />
                  </div>
                )
              }

              const children = (
                <>
                  {item.title}
                  <Icon
                    size="small"
                    type="chevron-right"
                    className={classNames(style.chevron, style.icon)}
                    data-test-id="main-navdrawer-expand-icon"
                  />
                </>
              )

              return i === path?.[0] ? (
                <div
                  key={i}
                  className={classNames(
                    item.className,
                    style.item,
                    style.active
                  )}
                >
                  {children}
                </div>
              ) : (
                <button
                  key={i}
                  className={classNames(item.className, style.item, style.link)}
                  data-test-id={`main-navdrawer-button-${depth}-${i}`}
                  onClick={() => {
                    setPath([i])
                    trackEvent(
                      analyticsUtils.categories.UNIVERSAL_HEADER,
                      analyticsUtils.actions.UNIVERSAL_HEADER_NAV_MENU_OPEN,
                      `L${depth}-${i} ${item.title}`
                    )
                  }}
                >
                  {children}
                </button>
              )
            }

            const x = { render: item }

            return <x.render key={i} />
          })}
        </Accordion>
        <div className={classNames(style['header-pane-mobile'])}>
          {onClose && (
            <button
              className={classNames(style.link, style.arrow)}
              onClick={onClose}
            >
              <Icon
                size="medium"
                type="arrow-left"
                className={classNames(style['action-icon'])}
              />
            </button>
          )}
          {onExit && (
            <button
              className={classNames(style.link, style.cross)}
              aria-label="Close Navigation"
              data-test-id="main-navdrawer-close"
              onClick={onExit}
            >
              <Icon
                size="small"
                type="cross"
                className={classNames(style['action-icon'])}
              />
            </button>
          )}
        </div>
      </div>
      {(() => {
        if (path?.length) {
          const i = path[0]
          const item = items[i]
          if (item && 'items' in item) {
            // added this condition for third level pane accordion
            if (depth !== 1) {
              return (
                <Pane
                  key={i}
                  depth={depth + 1}
                  title={item.hideTitle ? undefined : item.title}
                  items={item.items}
                  initialPath={path?.slice(1)}
                  onClose={() => {
                    setPath([])
                    trackEvent(
                      analyticsUtils.categories.UNIVERSAL_HEADER,
                      analyticsUtils.actions.UNIVERSAL_HEADER_NAV_MENU_CLOSE,
                      `L${depth}-${i} ${item.title}`
                    )
                  }}
                  onExit={onExit}
                />
              )
            }
          }
        }

        return null
      })()}
    </>
  )
}

export default Pane
